<template>
  <div class="identification-photos">
    <div v-if="!showEmptyState" class="attempt-switcher">
      <ui-button @click="showPreviousAttempt" :disabled="!hasPreviousAttempt">
        &minus;
      </ui-button>
      {{
        $t("media_attempt_indicator", {
          current: attempt || "&quest;",
          total: totalAttempts || "&quest;"
        })
      }}
      <ui-button @click="showNextAttempt" :disabled="!hasNextAttempt">
        &plus;
      </ui-button>
    </div>
    <div class="gallery">
      <transition name="transition-fade">
        <div v-show="showBackward" class="scroll-shadow start" />
      </transition>
      <transition name="transition-fade">
        <button
          v-show="showBackward"
          class="gallery-btn backward"
          @click="slideBackward"
        >
          <icon name="chevron-up" class="btn-icon" />
        </button>
      </transition>
      <div class="photos" ref="slider" @scroll.passive="slideButtonVisibility">
        <div v-if="showEmptyState" class="photo-item">
          <identification-photos-empty />
        </div>
        <div v-else-if="!media" class="photo-item placeholder">
          <identification-photo />
        </div>
        <div
          v-for="(selectedPicture, type) in selectedPicturesByType"
          :key="type"
          class="photo-item"
        >
          <identification-photo
            :key="selectedPicture.url"
            v-bind="selectedPicture"
          >
            <template v-slot:controls>
              <time
                :datetime="selectedPicture.created"
                v-if="mediaOptions[type] && mediaOptions[type].length < 2"
              >
                {{ documentDateFormat(new Date(selectedPicture.created)) }}
              </time>
              <ui-select
                v-else
                :options="mediaOptions[type]"
                v-model="selectedPhoto[type]"
              />
            </template>
          </identification-photo>
        </div>
      </div>
      <transition name="transition-fade">
        <div class="scroll-shadow end" v-show="showForward" />
      </transition>
      <transition name="transition-fade">
        <button
          v-show="showForward"
          class="gallery-btn forward"
          @click="slideForward"
        >
          <span v-if="unseenPhotoCount" class="unseen-photo-warn">
            {{
              $tc("media_unseen_photo", unseenPhotoCount, {
                count: unseenPhotoCount
              })
            }}
          </span>
          <icon name="chevron-down" class="btn-icon" />
        </button>
      </transition>
    </div>
  </div>
</template>

<script>
import throttle from "lodash/throttle";
import IdentificationPhoto from "@/components/IdentificationPhoto";
import IdentificationPhotosEmpty from "@/components/IdentificationPhotosEmpty";
import { mapGetters } from "vuex";
import { getters } from "@/store";
import { documentDateFormat } from "@/utils/date";

export default {
  data() {
    return {
      attempt: 0,
      selectedPhoto: {},
      showForward: false,
      showBackward: false,
      unseenPhotoCount: null,
      slideButtonVisibility: throttle(() => {
        const sliderEl = this.$refs.slider;
        const sliderHeight = sliderEl?.getBoundingClientRect().height;
        const currentScroll = Math.ceil(sliderEl.scrollTop);
        const maxScroll = Math.floor(sliderEl.scrollHeight - sliderHeight);
        this.showForward = currentScroll < maxScroll;
        this.showBackward = currentScroll > 0;
        if (this.unseenPhotoCount !== 0) {
          const itemCount = this.mediaCount;
          const totalArea = maxScroll + sliderHeight;
          const passedArea = currentScroll + sliderHeight;
          this.unseenPhotoCount = Math.round(
            itemCount - (itemCount / totalArea) * passedArea
          );
        }
      }, 200)
    };
  },
  computed: {
    ...mapGetters([
      getters.currentIdentificationMediaByType,
      getters.currentDocumentData,
      getters.isPhotoIdentification
    ]),
    showEmptyState() {
      return !this.isPhotoIdentification && !this.mediaCount;
    },
    minimumAttempt() {
      return this.currentIdentificationMediaByType?.[0] ? 0 : 1;
    },
    totalAttempts() {
      return Math.max(0, ...Object.keys(this.currentIdentificationMediaByType));
    },
    hasPreviousAttempt() {
      return this.attempt > this.minimumAttempt;
    },
    hasNextAttempt() {
      return this.attempt < this.totalAttempts;
    },
    currentAttemptMedia() {
      return this.currentIdentificationMediaByType?.[this.attempt];
    },
    mediaOptions() {
      return (
        this.currentAttemptMedia &&
        Object.entries(this.currentAttemptMedia).reduce(
          (optionsByType, [key, media]) => {
            optionsByType[key] = media
              .sort((a, b) => {
                if (a.created > b.created) return -1;
                if (a.created < b.created) return 1;
                return 0;
              })
              .map(({ url, created }) => ({
                label: this.documentDateFormat(new Date(created)),
                value: url
              }));
            return optionsByType;
          },
          {}
        )
      );
    },
    media() {
      return this.currentAttemptMedia;
    },
    selectedPicturesByType() {
      if (!this.media) {
        return null;
      }

      return Object.entries(this.media).reduce(
        (picturesByType, [type, photos]) => {
          const selectedUrl = this.selectedPhoto[type];
          const selectedPicture =
            photos.find(({ url }) => url === selectedUrl) || photos[0];

          if (selectedPicture) {
            picturesByType[type] = selectedPicture;
          }

          return picturesByType;
        },
        {}
      );
    },
    mediaCount() {
      return this.media && Object.values(this.media).length;
    }
  },
  watch: {
    totalAttempts: {
      handler() {
        this.attempt = this.totalAttempts;
      },
      immediate: true
    },
    attempt() {
      this.unseenPhotoCount = null;
      this.$nextTick(this.slideButtonVisibility);
    },
    mediaOptions: {
      handler() {
        this.selectedPhoto =
          this.mediaOptions &&
          Object.entries(this.mediaOptions).reduce(
            (selectedPhoto, [key, media]) => {
              selectedPhoto[key] = media[0]?.value;
              return selectedPhoto;
            },
            {}
          );
      },
      immediate: true
    }
  },
  components: {
    IdentificationPhoto,
    IdentificationPhotosEmpty
  },
  mounted() {
    this.slideButtonVisibility();
    window.addEventListener("resize", this.slideButtonVisibility);
  },
  beforeDestroy() {
    this.slideButtonVisibility.cancel();
    window.removeEventListener("resize", this.slideButtonVisibility);
  },
  methods: {
    showPreviousAttempt() {
      if (this.hasPreviousAttempt) {
        this.attempt--;
      }
    },
    showNextAttempt() {
      if (this.hasNextAttempt) {
        this.attempt++;
      }
    },
    documentDateFormat(date) {
      return documentDateFormat(date);
    },
    slideBackward() {
      const sliderEl = this.$refs.slider;
      const slideEl = sliderEl?.children[0];
      const slideHeight = slideEl?.getBoundingClientRect().height;
      const currentScroll = sliderEl.scrollTop;
      sliderEl.scrollTop = Math.max(0, currentScroll - slideHeight);
    },
    slideForward() {
      const sliderEl = this.$refs.slider;
      const sliderHeight = sliderEl?.getBoundingClientRect().height;
      const slideEl = sliderEl?.children[0];
      const slideHeight = slideEl?.getBoundingClientRect().height;
      const currentScroll = sliderEl.scrollTop;
      const maxScroll = sliderEl.scrollHeight - sliderHeight;
      sliderEl.scrollTop = Math.min(maxScroll, currentScroll + slideHeight);
    }
  }
};
</script>

<style scoped lang="scss">
.identification-photos {
  display: grid;
  grid-template-rows: auto 1fr;
  max-height: calc(100vh - 2rem);
}
.attempt-switcher {
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 1rem;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
}
.gallery {
  position: relative;
  z-index: 0;
  height: 100%;
  min-height: 0;
  text-align: center;
}
.photos {
  display: inline-flex;
  flex-direction: column;
  flex-wrap: nowrap;
  max-width: 100%;
  height: 100%;
  overflow: auto;
  scroll-behavior: smooth;
}
.photo-item {
  padding: 0.5rem;
  &.placeholder {
    visibility: hidden;
  }
}
.gallery-btn {
  position: absolute;
  left: 50%;
  z-index: 2;
  width: 3rem;
  height: 3rem;
  padding: 0;
  color: var(--button-default-text-color);
  background: var(--card-background-transparent);
  border: 0;
  border-radius: 50%;
  box-shadow: 0.1rem 0 0.25rem 0 var(--shadow-color);
  opacity: 0.7;
  transition-duration: var(--animation-fast);
  transition-property: opacity;
  transform: translateX(-50%);
  &:hover {
    opacity: 1;
  }
  &.backward {
    top: 0.25rem;
    border-top-width: 0;
  }
  &.forward {
    bottom: 0.25rem;
    border-bottom-width: 0;
  }
}
.unseen-photo-warn {
  position: absolute;
  bottom: 100%;
  left: 50%;
  display: block;
  padding: 0.25rem;
  margin-bottom: 0.5rem;
  white-space: nowrap;
  pointer-events: none;
  background: var(--red);
  border-radius: 0.25rem;
  transform: translateX(-50%);
}
.btn-icon {
  fill: currentColor;
}
.scroll-shadow {
  position: absolute;
  right: 0;
  left: 0;
  z-index: 1;
  height: 0.5rem;
  pointer-events: none;

  &.start {
    top: 0;
    background: linear-gradient(0deg, transparent 0%, var(--shadow-color) 100%);
  }
  &.end {
    bottom: 0;
    background: linear-gradient(0deg, var(--shadow-color) 0%, transparent 100%);
  }
}
.transition-fade {
  &-enter-active,
  &-leave-active {
    pointer-events: none;
    transition-duration: 0.5s;
    transition-property: opacity;
  }
  &-enter-to,
  &-leave {
    opacity: 1;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
}
</style>
